import persistCalendarsSelection from './persistCalendarsSelection';
import { getItemsForFolder } from 'owa-calendar-surface-events-lock-store';
import {
    getCalendars,
    isSelectedCalendar,
    selectCalendar as selectCalendarAction,
} from 'owa-calendar-surface-calendars-store';
import { getCalendarSurfaceStore } from 'owa-calendar-surface-surface-store';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { lazyPrefetchInsights } from 'owa-calendar-related-content-initialization';
import { calculateRequestRange } from 'owa-calendar-surface';
import { addCalendarItems } from 'owa-calendar-surface-dategrid';
import { DateRange } from 'owa-datetime-utils';
import * as trace from 'owa-trace';
import {
    Calendar,
    calendarFolderIdNeedsUpdate,
    getCalendarFolderId,
    getCalendarEntryByFolderId,
} from 'owa-calendar-cache';
import { getAndUpdateActualFolderId } from 'owa-calendarsapi-outlook';
import { CalendarEvent } from 'owa-calendar-types';
import {
    shouldPrefetchInsights,
    InsightsDataFetchSource,
} from 'owa-meeting-insights-types-and-flights';
import { linkedCalendarSelected } from './internalActions';
import { isOldModelSharedCalendar } from 'owa-calendar-properties';
import { isConnectedAccount } from 'owa-accounts-store';
import {
    lazyHasCurrentlyDisplayedView,
    lazyUpdateCurrentlyDisplayedViewId,
} from 'owa-save-calendar-view-data';
import { isFeatureEnabled } from 'owa-feature-flags';

/** dictionary of pending actions by folderId */
export let pendingActions: { [key: string]: Promise<CalendarEvent[]> } = {};

export async function selectCalendarWithId(id: string): Promise<boolean> {
    const [calendar] = getCalendars().filter(cal => cal.id.id.toLowerCase() === id.toLowerCase());

    if (calendar) {
        await selectCalendar(calendar);
        return true;
    }

    return false;
}

/**
 * Action to select calendar
 */
export let selectCalendar = createLegacyActionWithDatapoint('SelectCalendar', {
    name: 'SelectCalendar',
    options: {
        isCore: true,
    },
})(async function selectCalendar(
    calendar: Calendar,
    doNotPersist?: boolean,
    forceReset?: boolean
): Promise<void> {
    if (isFeatureEnabled('cal-saveCalendarView')) {
        const hasCurrentlyDisplayedView = lazyHasCurrentlyDisplayedView.tryImportForRender();
        const updateCurrentlyDisplayedViewId = lazyUpdateCurrentlyDisplayedViewId.tryImportForRender();
        if (updateCurrentlyDisplayedViewId && hasCurrentlyDisplayedView?.()) {
            updateCurrentlyDisplayedViewId(null);
        }
    }
    if (isSelectedCalendar(calendar)) {
        trace.trace.info(`[selectCalendar] Calendar is already selected: ${calendar.id}`);
        return;
    }

    // Replace with the correct calendar id for LinkedCalendarEntries before we add to selectedCalendars
    let folderIdUpdated = false;
    if (calendarFolderIdNeedsUpdate(calendar.id.id)) {
        folderIdUpdated = true;
        await getAndUpdateActualFolderId(calendar.id.id);
    }

    trace.trace.info(`[selectCalendar] Selecting calendar: ${calendar.id}`);
    selectCalendarAction(calendar);

    let folderId = getCalendarFolderId(calendar);
    if (!folderId) {
        trace.errorThatWillCauseAlert(
            `[selectCalendar] Unable to select calendar ${
                calendar.id
            }. folderIdUpdated value: ${folderIdUpdated}, connectedAccount value: ${isConnectedAccount(
                calendar.id?.mailboxInfo?.userIdentity
            )}`
        );
        return;
    }
    const items = await getItemsForFolderId(folderId, calendar, doNotPersist);

    if (isOldModelSharedCalendar(getCalendarEntryByFolderId(folderId)) && !forceReset) {
        linkedCalendarSelected(true);
    }

    if (items && items.length > 0 && shouldPrefetchInsights()) {
        lazyPrefetchInsights.importAndExecute(
            items,
            InsightsDataFetchSource.PrefetchOnCalendarSelected
        );
    }
});

async function getItemsForFolderId(folderId: string, calendar: Calendar, doNotPersist?: boolean) {
    const surfaceStore = getCalendarSurfaceStore();
    let requestDateRange: DateRange = calculateRequestRange(
        surfaceStore.selectedDate,
        surfaceStore.dateRange,
        surfaceStore.dateRangeType,
        surfaceStore.userOptions.weekStartDay,
        surfaceStore.calendarItems.length == 0 /* initial load */
    );
    let promiseResult = getItemsForFolder(requestDateRange, folderId).then(items => {
        // check if results came for last registered action
        if (promiseResult == pendingActions[folderId]) {
            // ensure that calendar is still selected before adding items
            if (isSelectedCalendar(calendar)) {
                addCalendarItems(items, surfaceStore);
            }
            delete pendingActions[folderId];
        }
        return items;
    });

    pendingActions[folderId] = promiseResult;

    if (!doNotPersist) {
        await persistCalendarsSelection(calendar.id.mailboxInfo.userIdentity);
    }
    return promiseResult;
}

export default selectCalendar;
